import React, { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, MapPin, Calendar, Trophy, ListChecks, Globe, Flag } from 'lucide-react'
import { useTranslation } from '../hooks/use-i18n'
import { formatPoints } from '../utils/points-label'
import toast from 'react-hot-toast'
import { isFormula1RaceUpcoming } from '../utils/formula1RaceTiming'
import {
  getActivePublicTournaments,
  getMyPublicEnrollments,
  getMyUpcomingRaces,
  getPublicLeaderboard,
  getMyPublicFormula1Predictions,
  QUERY_KEY_MY_PUBLIC_ENROLLMENTS,
  QUERY_KEY_PUBLIC_TOURNAMENTS,
  type LeaderboardScope,
  type UpcomingRaceItem,
  type PublicFormula1PredictionItem,
} from '../services/publicService'
import type { LeaderboardEntry } from '../types/public'
import PublicFormula1RaceResultModal from '../components/PublicFormula1RaceResultModal'
import InvitePlayerModal from '../components/InvitePlayerModal'
import PlayPublicModal from '../components/PlayPublicModal'
import EditPublicLocationModal from '../components/EditPublicLocationModal'
import { asPublicEnrollmentList, findActiveFormula1Participant, findPublicLocationFromEnrollments } from '../utils/publicFootballEnrollment'
import { syncWorldEnrollCache } from '../utils/worldEnrollCache'

type TabId = 'play' | 'results' | 'rankings'

function tournamentDisplayName(name: string): string {
  if (!name) return name
  const prefixes = [
    'Public Formula 1 – ',
    'Public Formula 1 - ',
    'World Formula 1 – ',
    'World Formula 1 - ',
  ]
  for (const prefix of prefixes) {
    if (name.startsWith(prefix)) return name.slice(prefix.length).trim()
  }
  return name
}

const PublicFormula1Page: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab') as TabId | null
  const initialTab: TabId = tabParam === 'play' || tabParam === 'results' || tabParam === 'rankings' ? tabParam : 'play'
  const [activeTab, setActiveTab] = useState<TabId>(initialTab)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showPlayPublicModal, setShowPlayPublicModal] = useState(false)
  const [showEditLocationModal, setShowEditLocationModal] = useState(false)

  const { t } = useTranslation()
  const queryClient = useQueryClient()

  useEffect(() => {
    const t = searchParams.get('tab') as TabId | null
    if (t === 'play' || t === 'results' || t === 'rankings') setActiveTab(t)
  }, [searchParams])

  const setTab = (tab: TabId) => {
    setActiveTab(tab)
    setSearchParams({ tab })
  }

  // Use same query key as Dashboard so after "Play Public" enroll we get fresh data when navigating here
  const { data: enrollmentsRaw, isLoading: loadingEnrollments } = useQuery({
    queryKey: QUERY_KEY_MY_PUBLIC_ENROLLMENTS,
    queryFn: getMyPublicEnrollments,
    staleTime: 0,
  })

  // F1 tournaments: useQuery with shared key so cache is reused across app
  const { data: f1Tournaments = [] } = useQuery({
    queryKey: [QUERY_KEY_PUBLIC_TOURNAMENTS, 'formula1'],
    queryFn: () => getActivePublicTournaments('formula1'),
    staleTime: 2 * 60 * 1000,
  })

  const enrollments = asPublicEnrollmentList(enrollmentsRaw)
  const f1Enrollment = findActiveFormula1Participant(enrollments) ?? null
  const hasF1Enrollment = !!f1Enrollment
  const existingLocation = findPublicLocationFromEnrollments(enrollments)

  const enrolledTournament =
    f1Enrollment?.participantTournaments?.find((pt) => pt.publicTournament)?.publicTournament
    ?? f1Tournaments.find((t) =>
      (f1Enrollment?.participantTournaments ?? []).some((pt) => pt.publicTournamentId === t.id),
    )
    ?? f1Tournaments[0]
  const firstTournament = enrolledTournament
  const scopeParticipant = f1Enrollment
    ? { countryCode: f1Enrollment.countryCode, city: f1Enrollment.city ?? undefined, continentCode: (f1Enrollment as any).continentCode }
    : null

  const refetchEnrollments = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_MY_PUBLIC_ENROLLMENTS })
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY_PUBLIC_TOURNAMENTS, 'formula1'] })
  }

  if (loadingEnrollments && !hasF1Enrollment) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  if (!hasF1Enrollment) {
    return (
      <div className="min-h-screen bg-background">
        <main className="pt-16 container mx-auto px-4 pb-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft size={18} /> {t('dashboard.backToDashboard')}
          </Link>
          <div className="glass-card rounded-2xl p-8 text-center max-w-md mx-auto">
            <p className="text-foreground mb-4">{t('publicFormula1.notEnrolled')}</p>
            <button
              type="button"
              onClick={() => setShowPlayPublicModal(true)}
              className="btn-primary px-4 py-2 rounded-xl w-full mb-2"
            >
              🏎️ {t('dashboard.playWorldFormula1')}
            </button>
            <Link to="/dashboard" className="inline-block text-sm text-muted-foreground hover:text-foreground">
              {t('publicFootball.goToDashboard')}
            </Link>
          </div>
        </main>
        {showPlayPublicModal && (
          <PlayPublicModal
            onlyGameType="formula1"
            initialCountryCode={existingLocation?.countryCode}
            initialCity={existingLocation?.city}
            onClose={() => setShowPlayPublicModal(false)}
            onSuccess={async (_tournamentId, gameType, extras) => {
              setShowPlayPublicModal(false)
              await syncWorldEnrollCache(queryClient, gameType ?? 'formula1', extras)
            }}
          />
        )}
      </div>
    )
  }

  const tabs: { id: TabId; label: string }[] = [
    { id: 'play', label: t('publicFootball.tabPlayUpcoming') },
    { id: 'results', label: t('publicFootball.tabMyResults') },
    { id: 'rankings', label: t('publicFootball.tabMyRankings') },
  ]

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16 container mx-auto px-4 pb-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft size={18} /> {t('dashboard.backToDashboard')}
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="font-display text-xl sm:text-2xl font-bold text-foreground">
            {t('publicFormula1.title')}
          </h1>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setShowInviteModal(true)}
              className="text-xs text-foreground hover:text-primary px-2.5 py-1 rounded-lg bg-primary/10"
            >
              {t('invitations.inviteFriend')}
            </button>
            <button
              type="button"
              onClick={() => setShowEditLocationModal(true)}
              className="text-xs text-muted-foreground hover:text-foreground px-2.5 py-1 rounded-lg bg-muted/50"
            >
              {t('publicFootball.editPreferences')}
            </button>
          </div>
        </div>
        <EditPublicLocationModal
          isOpen={showEditLocationModal}
          onClose={() => setShowEditLocationModal(false)}
          participant={f1Enrollment}
          onSaved={refetchEnrollments}
        />
        {showInviteModal && (
          <InvitePlayerModal
            kind="world_formula1"
            onClose={() => setShowInviteModal(false)}
            onSuccess={() => setShowInviteModal(false)}
          />
        )}

        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setTab(tab.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                activeTab === tab.id ? 'bg-primary text-primary-foreground' : 'bg-muted/20 text-foreground hover:bg-muted/40'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'play' && <PlayUpcomingTab />}
        {activeTab === 'results' && f1Enrollment && <MyResultsTab participantId={f1Enrollment.id} />}
        {activeTab === 'rankings' && firstTournament && (
          <MyRankingsTab
            publicTournamentId={firstTournament.id}
            tournamentName={firstTournament.name}
            scopeParticipant={scopeParticipant}
          />
        )}
      </main>
    </div>
  )
}

function PlayUpcomingTab() {
  const { t } = useTranslation()
  const [items, setItems] = useState<UpcomingRaceItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getMyUpcomingRaces()
      .then((list) => {
        if (!cancelled) setItems(list.filter((item) => isFormula1RaceUpcoming(item.race)))
      })
      .catch(() => { if (!cancelled) setItems([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const normalizeRace = (r: UpcomingRaceItem['race']) => {
    const race = { ...r }
    if (typeof race.drivers === 'string') {
      try { race.drivers = JSON.parse(race.drivers) } catch { race.drivers = [] }
    }
    if (typeof race.cars === 'string') {
      try { race.cars = JSON.parse(race.cars) } catch { race.cars = [] }
    }
    if (!Array.isArray(race.drivers)) race.drivers = []
    if (!Array.isArray(race.cars)) race.cars = []
    return race
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }
  if (items.length === 0) {
    return (
      <p className="text-muted-foreground py-6">{t('publicHub.noUpcomingMatches')}</p>
    )
  }

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">{t('publicFormula1.playUpcomingHint')}</p>
      <div className="space-y-4">
        {items.map((item) => {
          const race = normalizeRace(item.race)
          const raceId = race.id ?? (race as any)._id ?? ''
          return (
            <div key={raceId} className="glass-card rounded-xl p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-primary/20 text-primary">
                  {tournamentDisplayName(item.tournamentName)}
                </span>
                <span className="text-sm text-muted-foreground">
                  {race.raceDate ? new Date(race.raceDate).toLocaleDateString() : ''} {race.raceTime ?? ''}
                </span>
              </div>
              <div className="font-medium text-foreground mb-2">
                Race {race.day}: {race.city}
                {race.country ? `, ${race.country}` : ''}
              </div>
              <Link
                to={`/public/formula1/play/${raceId}`}
                className="inline-block px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
              >
                {t('publicHub.makePrediction')}
              </Link>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function MyResultsTab({ participantId }: { participantId: string }) {
  const { t } = useTranslation()
  const [predictions, setPredictions] = useState<PublicFormula1PredictionItem[]>([])
  const [loading, setLoading] = useState(true)
  const [detailModal, setDetailModal] = useState<PublicFormula1PredictionItem | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getMyPublicFormula1Predictions(participantId)
      .then((list) => {
        if (!cancelled) setPredictions(list)
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load results')
        if (!cancelled) setPredictions([])
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [participantId])

  const withRace = predictions.filter((p) => p.race)
  const sorted = [...withRace].sort((a, b) => {
    const dayA = a.race?.day ?? 0
    const dayB = b.race?.day ?? 0
    return dayB - dayA
  })

  const hasActualResults = (item: PublicFormula1PredictionItem) =>
    item.race?.actualResults && Object.keys(item.race.actualResults).length > 0

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }
  if (sorted.length === 0) {
    return (
      <p className="text-muted-foreground py-6">{t('publicFootball.noResultsYet')}</p>
    )
  }

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">{t('publicFootball.myResultsHint')}</p>
      <div className="space-y-4">
        {sorted.map((item) => {
          const resultsIn = hasActualResults(item)
          const pts = item.totalPoints ?? 0
          return (
            <div key={item.id} className="glass-card rounded-lg sm:rounded-xl p-3 sm:p-4">
              <div className="space-y-1.5 sm:space-y-2">
                <div className="flex items-start justify-between gap-2 sm:gap-3">
                  <div className="flex items-center flex-wrap gap-2 min-w-0 flex-1">
                    <span className="font-semibold text-small sm:text-body text-foreground">
                      Race {item.race?.day}: {item.race?.city}{item.race?.country ? `, ${item.race.country}` : ''}
                    </span>
                    {resultsIn && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-lg bg-primary/20 text-primary font-semibold text-xs sm:text-small border border-primary/30 shrink-0">
                        {t('formula1.resultsAvailable')}
                      </span>
                    )}
                  </div>
                </div>
                {item.race?.raceDate && (
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(item.race.raceDate).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                    {item.race?.city ? ` · ${item.race.city}${item.race?.country ? `, ${item.race.country}` : ''}` : ''}
                  </div>
                )}
                <div className="text-primary font-medium text-small sm:text-body">
                  {t('publicFootball.myPrediction')}: {t('publicFormula1.myPredictionSaved')}
                </div>
                <div className="mt-2 sm:mt-3 flex flex-wrap items-center gap-3 sm:gap-4 text-small sm:text-body">
                  {item.race && (
                    <button
                      type="button"
                      onClick={() => setDetailModal(item)}
                      className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-muted/30 text-foreground font-medium hover:bg-muted/50 transition-colors"
                    >
                      <ListChecks className="w-4 h-4 shrink-0" />
                      {t('publicFormula1.viewDetails') ?? 'View details'}
                    </button>
                  )}
                  <span
                    className={`ml-auto inline-flex items-center px-3 py-1 rounded-lg font-bold ${
                      !resultsIn
                        ? 'bg-muted/30 text-muted-foreground'
                        : pts === 0
                          ? 'bg-destructive/20 text-destructive'
                          : 'bg-success/20 text-success'
                    }`}
                  >
                    {formatPoints(pts, t)}
                  </span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
      {detailModal?.race && (
        <PublicFormula1RaceResultModal
          isOpen={!!detailModal}
          onClose={() => setDetailModal(null)}
          prediction={detailModal}
          race={detailModal.race}
        />
      )}
    </div>
  )
}

function rankMedal(rank: number) {
  if (rank === 1) return '🥇'
  if (rank === 2) return '🥈'
  if (rank === 3) return '🥉'
  return null
}

function MyRankingsTab({
  publicTournamentId,
  tournamentName,
  scopeParticipant,
}: {
  publicTournamentId: string
  tournamentName: string
  scopeParticipant: { countryCode?: string; city?: string; continentCode?: string } | null
}) {
  const { t } = useTranslation()
  const [scope, setScope] = useState<LeaderboardScope>('world')
  const [entries, setEntries] = useState<LeaderboardEntry[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const filters =
      scope === 'country' && scopeParticipant?.countryCode
        ? { countryCode: scopeParticipant.countryCode }
        : scope === 'city' && scopeParticipant?.city
          ? { city: scopeParticipant.city }
          : scope === 'continent' && scopeParticipant?.continentCode
            ? { continentCode: scopeParticipant.continentCode }
            : undefined
    getPublicLeaderboard(publicTournamentId, scope, filters)
      .then((list) => { if (!cancelled) setEntries(list) })
      .catch(() => { if (!cancelled) setEntries([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [publicTournamentId, scope, scopeParticipant])

  const scopes: { key: LeaderboardScope; label: string; Icon: React.ComponentType<{ className?: string }> }[] = [
    { key: 'world', label: t('publicLeaderboard.world'), Icon: Globe },
    { key: 'country', label: t('publicLeaderboard.country'), Icon: Flag },
    { key: 'city', label: t('publicLeaderboard.city'), Icon: MapPin },
  ]

  const scopeTitle =
    scope === 'country' && entries.length > 0
      ? entries[0].countryName
      : scope === 'city' && (entries.length > 0 || scopeParticipant?.city)
        ? (entries[0]?.city ?? scopeParticipant?.city ?? '')
        : null

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-3">{t('publicFootball.myRankingsHint')}</p>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
          <Trophy className="w-5 h-5 text-primary" />
        </div>
        <p className="text-sm font-semibold text-foreground">{tournamentDisplayName(tournamentName)}</p>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {scopes.map((s) => {
          const Icon = s.Icon
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setScope(s.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                scope === s.key ? 'bg-primary text-primary-foreground' : 'bg-muted/20 text-foreground hover:bg-muted/30'
              }`}
            >
              <Icon className="w-4 h-4" />
              {s.label}
            </button>
          )
        })}
      </div>

      {scope === 'country' && scopeTitle && (
        <div className="flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 mb-4">
          <Flag className="w-5 h-5 text-primary shrink-0" />
          <span className="font-semibold text-foreground">{scopeTitle}</span>
        </div>
      )}
      {scope === 'city' && scopeTitle && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 mb-4">
          <MapPin className="w-5 h-5 text-emerald-500 shrink-0" />
          <span className="font-semibold text-foreground">{scopeTitle}</span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
      ) : entries.length === 0 ? (
        <div className="glass-card rounded-xl p-8 text-center border border-muted/20">
          <Trophy className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
          <p className="text-muted-foreground">{t('publicLeaderboard.noEntries')}</p>
        </div>
      ) : (
        <div className="space-y-2 sm:space-y-2.5">
          {entries.map((e, index) => {
            const medal = rankMedal(e.rank)
            const isTop3 = e.rank <= 3
            const colors = ['bg-primary', 'bg-destructive', 'bg-success', 'bg-warning', 'bg-chess', 'bg-primary', 'bg-destructive', 'bg-success']
            const color = colors[index % colors.length]
            const initial = (e.userName || '?').trim().charAt(0).toUpperCase()
            const locationLabel = scope === 'world' ? (e.countryCode || '').toUpperCase() : scope === 'country' ? (e.city ?? '') : ''
            return (
              <div
                key={e.publicParticipantId}
                className={`rounded-lg sm:rounded-xl p-2.5 sm:p-3.5 lg:p-4 flex items-center gap-2 sm:gap-3 lg:gap-4 transition-all hover:bg-muted/30 ${
                  isTop3 ? 'glass-card border border-primary/10' : 'glass-card'
                }`}
              >
                <span className="text-base sm:text-display-2 font-display font-bold w-6 sm:w-7 lg:w-8 text-center text-muted-foreground shrink-0">
                  {medal || e.rank}
                </span>
                <div className={`max-[479px]:hidden w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full ${color} flex items-center justify-center text-[0.65rem] sm:text-small font-bold text-primary-foreground shrink-0`}>
                  {initial}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-small sm:text-nav truncate text-foreground">{e.userName}</p>
                  <div className="flex items-center flex-wrap gap-2 sm:gap-3 mt-0.5">
                    <span className="text-small sm:text-nav font-semibold text-primary">{formatPoints(e.totalPoints, t)}</span>
                    <span className="text-small text-muted-foreground/60">·</span>
                    <span className="text-small text-muted-foreground">{e.predictionCount} {t('roomPage.pred')}</span>
                    {locationLabel && (
                      <>
                        <span className="text-small text-muted-foreground/60">·</span>
                        <span className="text-small text-muted-foreground inline-flex items-center gap-1">
                          {scope === 'world' ? <Flag className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                          {locationLabel}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default PublicFormula1Page
